import {
  createTheme,
  PaletteColorOptions,
  ThemeProvider,
} from "@mui/material/styles";
import { get } from 'lodash'

const defaultTheme = createTheme();

declare module "@mui/material/styles" {
  interface TypographyVariants {
    config_header: React.CSSProperties;
    form_text: React.CSSProperties;
    body3: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    config_header?: React.CSSProperties;
    form_text: React.CSSProperties;
    body3: React.CSSProperties;
  }

  interface Palette {
    white: string;
    cde: string;
    ssss: string;
    twin: string;
    dwss: string;
    finance: string;
    defaultButton: string;
  }

  interface PaletteOptions {
    white: PaletteColorOptions;
    cde: PaletteColorOptions;
    ssss: PaletteColorOptions;
    twin: PaletteColorOptions;
    dwss: PaletteColorOptions;
    finance: PaletteColorOptions;
    bim360: PaletteColorOptions;
    chatbot: PaletteColorOptions;
    gps: PaletteColorOptions;
    defaultButton: PaletteColorOptions;
    lightgrey: PaletteColorOptions;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    rounded: true;
    primaryRounded: true;
  }
  interface ButtonPropsColorOverrides {
    white: true;
    cde: true;
    ssss: true;
    twin: true;
    dwss: true;
    finance: true;
    defaultButton: true;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    config_header: true;
    form_text: true;
    body3: true;
  }
}

declare module "@mui/material/Switch" {
  interface SwitchPropsVariantOverrides { 
    rounded: true;
    smallRounded: true;
  }
}

declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    white: true;
    cde: true;
    ssss: true;
    twin: true;
    dwss: true;
    finance: true;
    defaultButton: true;
    info: true;
    lightgrey: true;
  }
}

export const theme = createTheme({
  palette: {
    white: {
      main: "#FFFFFF",
    },
    cde: {
      main: "#D77ECA",
    },
    ssss: {
      main: "#4BBE55",
    },
    twin: {
      main: "#5D7DC1",
    },
    dwss: {
      main: "#2891A0",
    },
    finance: {
      main: "#E8C843",
    },
    bim360: {
      main: "#EF9148",
    },
    chatbot: {
      main: "#D8AE50",
    },
    gps: {
      main: "#EA6565",
    },
    defaultButton: {
      main: "#6C91A5",
    },
    primary: {
      main: "#304A58",
    },
    secondary: {
      main: "#020F1D",
    },
    error: {
      main: "#bb1d1d",
    },
    info: {
      main: "#707070",
    },
    lightgrey: {
      main: '#D3D3D3',
    }
  },
  typography: {
    fontFamily: [
      "Inter",
      "Titillium Web",
      "Roboto",
      "Helvetica",
      "Arial",
      "sans-serif",
    ].join(","),
    h1: {
      fontWeight: "bold",
      fontSize: "36px",
    },
    h2: {
      fontWeight: "bold",
      fontSize: "24px",
    },
    h3: {
      fontWeight: 600,
      fontSize: "24px",
    },
    h4: {
      fontWeight: 600,
      fontSize: "20px",
    },
    h5: {
      fontWeight: 600,
      fontSize: "18px",
    },
    h6: {
      fontWeight: 600,
      fontSize: "16px",
    },
    body1: {
      fontWeight: 300,
      fontSize: "25px",
    },
    body2: {
      fontWeight: 300,
      fontSize: "12px",
    },
    body3: {
      fontWeight: 300,
      fontSize: "16px",
    },
    button: {
      textTransform: "none",
      fontWeight: 300,
    },
    config_header: {
      // fontSize: "2.125rem",
      fontSize: "1.5rem",
      fontWeight: "bold",
      color: "#707070",
    },
    form_text: {
      fontSize: "1.25rem",
      fontWeight: 300,
      color: "#707070",
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: ({ ownerState: { color }, theme: { palette } }) => {
          if(Object.keys(palette).some(x => x === color)){
            return { color: get(palette, `${color}.main`, 'inherit') }
          }else{
            return {}
          }
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState: { color }, theme: { palette } }) => ({
          color: color === 'white' ? get(palette, 'secondary.main') : get(palette, 'white.main'),
          borderRadius: "10px",
        }),
      },
      variants: [
        {
          props: { variant: "rounded" },
          style: {
            borderRadius: "50%",
            backgroundColor: "#FFFFFF",
            maxWidth: "42px",
            maxHeight: "42px",
            minWidth: "42px",
            minHeight: "42px",
            transition: "background-color .5s ease 100ms",
            "&:hover": {
              backgroundColor: "#D3D3D3",
            },
          },
        },
        {
          props: { variant: "primaryRounded" },
          style: {
            borderRadius: "50%",
            backgroundColor: defaultTheme.palette.primary.main,
            maxWidth: "52px",
            maxHeight: "52px",
            minWidth: "52px",
            minHeight: "52px",
            transition: "background-color .5s ease 100ms",
            "&:hover": {
              backgroundColor: "#0057A3",
            },
          },
        },
      ],
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          margin: "2px",
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          fontSize: "18px",
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "18px",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "8px"
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          fontWeight: "500",
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: "16px",
          fontWeight: "500",
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides:{
        noOptions: {
          fontSize: '16px'
        }
      }
    }
  },
});
